import { useState } from 'react';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { CalendarIcon, CalendarDays, Filter, X, UserCircle } from 'lucide-react';
import { format, subDays, startOfWeek, startOfDay, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

export const DateFilter = ({ date, setDate, placeholder, testId }) => (
    <Popover>
        <PopoverTrigger asChild>
            <Button
                variant="outline"
                className={`w-[160px] justify-start text-left font-normal ${!date && 'text-slate-400'}`}
                data-testid={testId}
            >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, 'dd/MM/yyyy') : placeholder}
            </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
            <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                locale={es}
                defaultMonth={new Date()}
                initialFocus
            />
        </PopoverContent>
    </Popover>
);

export const SingleDayFilter = ({ day, onSelectDay, testId, minDate }) => {
    const [open, setOpen] = useState(false);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={`min-w-[190px] justify-start text-left font-normal ${day ? 'border-sky-300 bg-sky-50 text-sky-700' : 'text-slate-400'}`}
                    data-testid={testId}
                    title="Filtrar un solo día"
                >
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {day ? format(day, "dd 'de' MMMM, yyyy", { locale: es }) : 'Día exacto'}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={day}
                    onSelect={(d) => { if (d) { onSelectDay(d); setOpen(false); } }}
                    locale={es}
                    defaultMonth={day || new Date()}
                    disabled={minDate ? { before: minDate } : undefined}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
};

export const QuickRangeButtons = ({ onSelectRange, activeFrom, activeTo, testIdPrefix }) => {
    const today = startOfDay(new Date());
    const ranges = [
        { key: 'today', label: 'Hoy', from: today, to: today },
        { key: 'yesterday', label: 'Ayer', from: subDays(today, 1), to: subDays(today, 1) },
        { key: 'week', label: 'Esta semana', from: startOfWeek(today, { weekStartsOn: 1 }), to: today },
    ];
    const isActive = (r) => activeFrom && activeTo && isSameDay(activeFrom, r.from) && isSameDay(activeTo, r.to);
    return (
        <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-quick-ranges`}>
            {ranges.map((r) => (
                <Button
                    key={r.key}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onSelectRange(r.from, r.to)}
                    className={`h-9 rounded-full px-3 text-xs ${isActive(r) ? 'bg-sky-500 border-sky-500 text-white hover:bg-sky-600 hover:text-white' : 'text-slate-600'}`}
                    data-testid={`${testIdPrefix}-quick-${r.key}`}
                >
                    {r.label}
                </Button>
            ))}
        </div>
    );
};

export const UserFilter = ({ users, selectedUserId, setSelectedUserId, testId }) => (
    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
        <SelectTrigger className="w-[200px]" data-testid={testId}>
            <UserCircle className="mr-2 h-4 w-4 text-slate-400" />
            <SelectValue placeholder="Filtrar por usuario" />
        </SelectTrigger>
        <SelectContent>
            <SelectItem value="all">Todos los usuarios</SelectItem>
            {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                    {u.name}
                </SelectItem>
            ))}
        </SelectContent>
    </Select>
);

export const FilterActions = ({ onFilter, onClear, hasActiveFilters, filterTestId, clearTestId }) => (
    <>
        <Button
            onClick={onFilter}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid={filterTestId}
        >
            <Filter className="w-4 h-4 mr-2" />
            Filtrar
        </Button>

        {hasActiveFilters && (
            <Button
                variant="ghost"
                onClick={onClear}
                className="text-slate-500 hover:text-slate-700"
                data-testid={clearTestId}
            >
                <X className="w-4 h-4 mr-1" />
                Limpiar
            </Button>
        )}
    </>
);
